import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { theme, cssVar } from 'styles/theme';

const Panel = styled.div`
  position: fixed;
  z-index: 10050;
  min-width: 220px;
  max-width: 420px;
  max-height: 240px;
  overflow: auto;
  background: ${cssVar('color-bg-white', theme.colors.white)};
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
  font-size: ${theme.typography.fontSize.sm};
`;

const OptionBtn = styled.button`
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 6px 10px;
  border: none;
  border-bottom: 1px solid ${cssVar('color-border-light', theme.colors.borderLight || '#f1f5f9')};
  background: ${(p) =>
    p.$active
      ? cssVar('color-primary-light', theme.colors.primaryLight || 'rgba(37, 99, 235, 0.1)')
      : 'transparent'};
  color: ${cssVar('color-text', theme.colors.text.primary)};
  text-align: left;
  cursor: pointer;
  font: inherit;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${cssVar('color-primary-light', theme.colors.primaryLight || 'rgba(37, 99, 235, 0.08)')};
  }
`;

const Code = styled.span`
  flex: 0 0 auto;
  min-width: 72px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${cssVar('color-primary', theme.colors.primary)};
`;

const Name = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
`;

const Empty = styled.div`
  padding: 8px 10px;
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
`;

/**
 * 코드 Suggest 드롭다운 (Portal)
 * @param {boolean} open
 * @param {{ top: number, left: number, width?: number }} anchorRect — getBoundingClientRect 기준
 * @param {Array<{ id: string, code: string, name: string }>} options
 * @param {number} highlightIndex
 * @param {(opt: object, index: number) => void} onSelect
 * @param {() => void} [onClose]
 * @param {boolean} [loading]
 */
const CodeSuggestPanel = ({
  open,
  anchorRect,
  options = [],
  highlightIndex = 0,
  onSelect,
  loading = false,
}) => {
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current[highlightIndex];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, highlightIndex]);

  if (!open || !anchorRect) return null;

  const width = Math.max(anchorRect.width || 220, 220);
  let top = (anchorRect.bottom || 0) + 2;
  let left = anchorRect.left || 0;
  if (typeof window !== 'undefined') {
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    if (top + 240 > window.innerHeight - 8) {
      top = Math.max(8, (anchorRect.top || 0) - 242);
    }
  }

  return createPortal(
    <Panel
      ref={listRef}
      role="listbox"
      data-code-suggest-panel
      style={{ top, left, width }}
      onMouseDown={(e) => {
        // 입력 blur 전에 선택 처리되도록 포커스 이동 방지
        e.preventDefault();
      }}
    >
      {loading && !options.length ? (
        <Empty>검색 중…</Empty>
      ) : !options.length ? (
        <Empty>검색 결과 없음</Empty>
      ) : (
        options.map((opt, idx) => (
          <OptionBtn
            key={opt.id || `${opt.code}-${idx}`}
            type="button"
            role="option"
            aria-selected={idx === highlightIndex}
            $active={idx === highlightIndex}
            ref={(el) => {
              itemRefs.current[idx] = el;
            }}
            onMouseEnter={() => {
              /* highlight는 키보드가 주, 호버는 클릭 유도만 */
            }}
            onClick={() => onSelect?.(opt, idx)}
          >
            <Code>{opt.code}</Code>
            <Name title={opt.name}>{opt.name}</Name>
          </OptionBtn>
        ))
      )}
    </Panel>,
    document.body
  );
};

export default CodeSuggestPanel;
